import { type ReactNode, useMemo, useState } from "react";
import type { FileRejection } from "react-dropzone";
import { useNavigate, useParams } from "react-router";
import { useMutation } from "@tanstack/react-query";
import type { FileResponse, TaskResponse } from "@patent-studio/api-types";
import { refusalDetail } from "@/services/api-client";
import { deleteFile } from "@/services/files";
import { useTask, useTaskRefresh } from "@/hooks/use-tasks";
import {
  type IntakeSlotsRead,
  SLOTS_NOT_READ,
  useIntakeSlots,
} from "@/hooks/use-files";
import { useStartExtraction } from "@/hooks/use-invention";
import {
  UploadComponent,
  describeRejection,
  type UploadedFile,
} from "@/components/shared/UploadComponent";
import { MicroLabel } from "@/components/ui/MicroLabel";
import type { IntakeComponentSpec } from "@/lib/intake-components";
import { documentFormatLabel } from "@/lib/constants";
import { fileStatusLabel, isAwaitingPages } from "@/lib/file-status";
import {
  CommittedDescription,
  SupportingTagPicker,
} from "../intake/SupportingTagPicker";
import { useSlotUploads } from "@/hooks/use-slot-uploads";
import {
  intakeFileId,
  intakeRule,
  intakeSlotId,
  type IntakeRule,
} from "../intake/advance-rule";
import { LoadFailure } from "@/components/shared/LoadFailure";
import { RetryAction } from "@/components/shared/RetryAction";
import { StageFooter } from "../layout/StageFooter";
import { stagedWarning } from "@/lib/supporting-description";
import { stageHeadingLabel, subStepPath } from "@/v2/stage-vocabulary";
import { StagePage } from "../layout/StagePage";
import { MUTATION_KEYS } from "@/lib/analytics/mutation-reporting";

/** The gate bar's heading once nothing is being saved: never "Materials ready" over no slots. */
function gateHeading(
  specs: readonly IntakeComponentSpec[] | undefined,
  rule: ReturnType<typeof intakeRule>,
  files: FileResponse[] | undefined,
): string {
  return specs === undefined
    ? "No upload slots yet"
    : rule.advanceHeading(files);
}

/** A slots table that could not be read, said with the way to read it again. */
function SlotsFailure({ slots }: { slots: IntakeSlotsRead }) {
  return slots.failure === null ? null : (
    <LoadFailure
      error={slots.failure}
      fallback={SLOTS_NOT_READ}
      onRetry={slots.retry}
    />
  );
}

/**
 * What a row carries under the filename: what the attorney said this file is,
 * and the control that reads it again.
 *
 * Both, not one or the other. A file the slot asked words about can also be a
 * file that would not read, and the two answer different questions. A file the
 * server has not offered a retry on gets no control at all rather than a
 * disabled one, since there is nothing to wait for on a file that was read.
 */
function fileExtra(
  taskId: string | undefined,
  onError: (message: string | null) => void,
) {
  return (held: UploadedFile) => (
    <>
      <CommittedDescription text={held.description} />
      {held.canRetry && taskId && (
        <div className="mt-1.5">
          <RetryAction
            taskId={taskId}
            fileId={held.id}
            filename={held.name}
            whenRefused={`Could not read ${held.name} again.`}
            onError={onError}
          />
        </div>
      )}
    </>
  );
}

/**
 * The props every slot sets the same way, whatever it holds.
 *
 * Two components render a slot because they draw from different places and
 * upload through different hooks, and a hook cannot be chosen by a condition.
 * What does not differ is gathered here, so the two cannot come to word a
 * refusal differently or disagree about which formats they advertise.
 */
function slotChrome(
  spec: IntakeComponentSpec,
  ordinal: number,
  error: string | null,
  onError: (name: string, message: string | null) => void,
) {
  return {
    id: intakeSlotId(spec),
    name: spec.name,
    title: spec.title,
    description: spec.description,
    acceptedLabels: spec.acceptedLabels,
    accept: spec.accept,
    required: spec.required,
    maxFiles: spec.maxFiles,
    ordinal,
    error,
    onRejected: (rejections: FileRejection[]) =>
      onError(
        spec.name,
        describeRejection(spec.title, spec.acceptedLabels, rejections),
      ),
  };
}

/** Said in the slot when the server would not remove a file and gave no reason. */
const NOT_REMOVED = "The file could not be removed.";

/**
 * One component's wiring: which files are its own, what a selection means, and
 * what to say when a file is refused.
 */
function IntakeSlot({
  spec,
  ordinal,
  files,
  taskId,
  error,
  onError,
  onSettled,
  onInFlight,
  onStaged,
  onRemove,
}: {
  spec: IntakeComponentSpec;
  ordinal: number;
  files: UploadedFile[];
  taskId: string | undefined;
  error: string | null;
  onError: (name: string, message: string | null) => void;
  onSettled: () => Promise<void>;
  /** +1 when an upload starts, -1 when it ends, so the page can wait on it. */
  onInFlight: (delta: number) => void;
  /** +n when files are staged, -1 as each leaves, staged or discarded. */
  onStaged: (delta: number) => void;
  /** Remove a file; a refusal is said through `refused`, in its slot. */
  onRemove: (fileId: string, refused: (message: string) => void) => void;
}) {
  const uploads = useSlotUploads({
    spec,
    taskId,
    source: "intake",
    onError: (message) => onError(spec.name, message),
    onSettled,
    onInFlight,
    onStaged,
  });

  return (
    <UploadComponent
      {...slotChrome(spec, ordinal, error, onError)}
      files={files}
      busy={uploads.busy}
      renderFileExtra={fileExtra(taskId, (message) =>
        onError(spec.name, message),
      )}
      describe={
        <SupportingTagPicker
          spec={spec}
          rows={uploads.pending}
          onChoose={uploads.choose}
          onWrite={uploads.write}
          onAdd={uploads.add}
          onCancel={uploads.discardAll}
        />
      }
      asking={uploads.pending.some((row) => row.status !== "sending")}
      onFilesSelected={uploads.select}
      onRemove={(fileId) => {
        onRemove(fileId, (message) => onError(spec.name, message));
      }}
    />
  );
}

/**
 * What a row says beside its name: the page count once the file is read, as
 * the design shows it, and until then where the reading stands. A Word or text
 * file has no pages of its own, so it says its format. A file a tag
 * stores as another type, as "Figures" stores a figure, is never read here:
 * an image is one view, and a PDF of drawings says how its pages are coming.
 */
function fileMeta(file: FileResponse, spec: IntakeComponentSpec): string {
  if (file.file_type !== spec.fileType) {
    return isAwaitingPages(file.processing_status)
      ? fileStatusLabel(file.processing_status)
      : "1 view";
  }
  if (file.processing_status !== "indexed") {
    return fileStatusLabel(file.processing_status);
  }
  if (!file.cites_pages) {
    return documentFormatLabel(file.content_type, file.original_filename);
  }
  return file.page_count !== null
    ? `${String(file.page_count)} pp`
    : fileStatusLabel(file.processing_status);
}

/**
 * A component's own files, in the shape the upload component renders: those
 * of its type, and those given one of its tags, which a tag may store as
 * another type, as "Figures" stores a figure.
 */
function toUploadedFiles(
  files: TaskResponse["files"] | undefined,
  spec: IntakeComponentSpec,
): UploadedFile[] {
  const tagOf = (f: FileResponse) =>
    spec.tags.find((tag) => tag.id === f.supporting_tag);
  return (files ?? [])
    .filter((f) => f.file_type === spec.fileType || tagOf(f) !== undefined)
    .map((f) => ({
      id: f.file_id,
      elementId: intakeFileId(f.file_id),
      name: f.original_filename,
      meta: fileMeta(f, spec),
      tag: tagOf(f)?.label,
      description: f.description,
      canRetry: f.can_retry,
    }));
}

/** The detail line once nothing is saving: the rule's words, then any staged-files warning. */
function settledDetail(
  rule: IntakeRule,
  files: TaskResponse["files"] | undefined,
  staged: number,
): string {
  return [rule.advanceDetail(files), stagedWarning(staged)]
    .filter(Boolean)
    .join(" ");
}

/** What the submit bar says while the page is waiting on a write. */
function savingCopy(inFlight: number): { heading: string; detail: string } {
  return inFlight > 0
    ? {
        heading: "Finishing your uploads",
        detail: "The last file is still on its way up.",
      }
    : {
        heading: "Updating your documents",
        detail: "Just finishing that change.",
      };
}

/** The words above each group of slots, as the design writes them. */
const GROUPS = [
  {
    required: true,
    heading: "Required",
    intro:
      "The invention disclosure is needed to make a start on the patent draft. Nothing is read, planned or generated until it is uploaded, so this is the one thing to add first.",
  },
  {
    required: false,
    heading: "Optional at This Stage",
    intro:
      "Add any of these now if you have them to hand. None are required to get started, and you can upload them later at any point in the journey.",
  },
] as const;

/** One group of slots under its heading and the sentence that says what it is for. */
function SlotGroup({
  heading,
  intro,
  children,
}: {
  heading: string;
  intro: string;
  children: ReactNode;
}) {
  return (
    <section aria-label={heading} className="mt-7 first:mt-0">
      <MicroLabel as="h2" size="lg">
        {heading}
      </MicroLabel>
      <p className="mb-3.5 mt-1.5 max-w-[64ch] text-[12.5px] leading-relaxed text-muted-foreground">
        {intro}
      </p>
      {children}
    </section>
  );
}

/**
 * Every slot the API serves, the required ones first, each group under its
 * heading. A slot keeps its number across the groups, so the attorney reads
 * one run of steps from top to bottom.
 */
function IntakeSlots({
  specs,
  taskId,
  task,
  errors,
  onError,
  onRemove,
  onSettled,
  onInFlight,
  onStaged,
}: {
  specs: readonly IntakeComponentSpec[];
  taskId: string | undefined;
  task: TaskResponse | undefined;
  errors: Record<string, string | null>;
  onError: (name: string, message: string | null) => void;
  /** Remove a file; a refusal is said through `refused`, in its slot. */
  onRemove: (fileId: string, refused: (message: string) => void) => void;
  onSettled: () => Promise<void>;
  onInFlight: (delta: number) => void;
  onStaged: (delta: number) => void;
}) {
  const ordered = GROUPS.flatMap((group) =>
    specs.filter((spec) => spec.required === group.required),
  );
  return (
    <div className="mt-12">
      {GROUPS.map((group) => {
        const members = ordered.filter((s) => s.required === group.required);
        if (members.length === 0) return null;
        return (
          <SlotGroup
            key={group.heading}
            heading={group.heading}
            intro={group.intro}
          >
            {members.map((spec) => (
              <IntakeSlot
                key={spec.name}
                spec={spec}
                ordinal={ordered.indexOf(spec) + 1}
                files={toUploadedFiles(task?.files, spec)}
                taskId={taskId}
                error={errors[spec.name] ?? null}
                onError={onError}
                onSettled={onSettled}
                onInFlight={onInFlight}
                onStaged={onStaged}
                onRemove={onRemove}
              />
            ))}
          </SlotGroup>
        );
      })}
    </div>
  );
}

/** Takes one file off the draft, then reads the task again. */
function useRemoveFile(
  taskId: string | undefined,
  refresh: () => Promise<void>,
) {
  const removal = useMutation({
    mutationKey: MUTATION_KEYS.fileDelete,
    mutationFn: (fileId: string) =>
      taskId
        ? deleteFile(taskId, fileId)
        : Promise.reject(new Error("No draft selected")),
    onSuccess: refresh,
  });
  return {
    isPending: removal.isPending,
    /** Remove a file, and say a refusal through `refused`, in its slot. */
    remove: (fileId: string, refused: (message: string) => void) => {
      removal.mutate(fileId, {
        onError: (error) => {
          refused(refusalDetail(error, NOT_REMOVED));
        },
      });
    },
  };
}

/**
 * Stage 1 — everything the tool plans from.
 *
 * Files are server state: persisted the moment they are chosen and read back
 * from `TaskResponse.files`. Nothing here reads a file for meaning; the worker
 * does that after upload and reports back over SSE.
 */
export function IntakeStagePane() {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const { data: task } = useTask(taskId ?? null);
  const startExtraction = useStartExtraction(taskId ?? null);
  const [errors, setErrors] = useState<Record<string, string | null>>({});
  // The attorney has asked to move on; the page is waiting on the worker.
  // A file mid-POST is not on the task yet, so the rule cannot see it. Without
  // this the control would open while a reference was still on its way up.
  const [inFlight, setInFlight] = useState(0);
  // Files chosen and described but not sent. They live in the slots as
  // client-only state, so nothing else on this page can see them, and leaving
  // takes them with it.
  const [staged, setStaged] = useState(0);

  const refresh = useTaskRefresh(taskId);
  const remove = useRemoveFile(taskId, refresh);

  // The slots the API serves, and the gate built over them. Until the table
  // arrives there are no slots to draw and nothing the gate can judge.
  const slots = useIntakeSlots();
  const specs = slots.specs;
  const rule = useMemo(() => intakeRule(specs ?? []), [specs]);

  const setError = (name: string, message: string | null) =>
    setErrors((current) => ({ ...current, [name]: message }));

  // Anything the attorney has changed but the server has not confirmed. The
  // task still reports the old set of files until the refetch lands, so the
  // rule would otherwise judge a draft that is about to change.
  const saving = inFlight > 0 || remove.isPending;
  const { heading: savingHeading, detail: savingDetail } = savingCopy(inFlight);
  const ready = specs !== undefined && !saving && rule.canSubmit(task?.files);

  return (
    <StagePage
      guide="intake"
      testId="intake-stage-pane"
      taskId={taskId}
      eyebrow={stageHeadingLabel("intake")}
      title="Upload Materials"
      lede={
        <>
          Everything the tool plans from.{" "}
          <strong>Start with what you have</strong>: upload the invention
          disclosure to begin, and add more at any point. This is where the
          draft begins, not a final submission.
        </>
      }
    >
      <SlotsFailure slots={slots} />
      {specs !== undefined && (
        <IntakeSlots
          specs={specs}
          taskId={taskId}
          task={task}
          errors={errors}
          onError={setError}
          onRemove={remove.remove}
          onSettled={refresh}
          onInFlight={(delta) => {
            setInFlight((n) => n + delta);
          }}
          onStaged={(delta) => {
            setStaged((n) => n + delta);
          }}
        />
      )}

      <StageFooter
        label="Continue"
        pendingLabel="Checking your documents…"
        testId="intake-submit-bar"
        heading={saving ? savingHeading : gateHeading(specs, rule, task?.files)}
        detail={
          saving ? savingDetail : settledDetail(rule, task?.files, staged)
        }
        ready={ready}
        sticky
        wash
        conditions={saving ? [] : rule.advanceConditions(task?.files)}
        resolveTarget={(c) => c.first_id}
        onSubmit={() => {
          if (!taskId) return;
          // Fired and left running. The attorney reads the invention on the
          // next page before the pass could plausibly finish; nothing here
          // waits on it.
          startExtraction.mutate();
          void navigate(subStepPath(taskId, "intake", "classification"));
        }}
      />
    </StagePage>
  );
}
